import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import {
    DataGrid, GridToolbar, GridToolbarContainer,
    GridToolbarColumnsButton,
    GridToolbarFilterButton,
    GridToolbarExport,
    GridToolbarDensitySelector,
} from '@mui/x-data-grid';
import useApiManager from '../../../utils/Api/apiManager';
import { useNavigate, Link } from "react-router-dom";
import { LeadIndustry, LeadRating, LeadSource, LeadStatus } from '../../../utils/enums'
import FilterComponent from './FilterComponent';

function CustomToolbar() {
    return (
        <GridToolbarContainer>
            <GridToolbarColumnsButton />
            <GridToolbarDensitySelector />
        </GridToolbarContainer>
    );
}

const LeadsDataGrid = ({ filters, selectedRows, onSelectionModelChange, campaignId, triggerFetch, setTriggerFetch }) => {
    const [rows, setRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sortModel, setSortModel] = useState([]);
    const navigate = useNavigate();
    const { post } = useApiManager();
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 50,
    });
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [filterParams, setFilterParams] = useState([]);

    const requestIdRef = useRef(0);

    const columns = [
        { field: 'id', headerName: 'ID', flex: 0.5 },
        {
            field: 'firstName',
            headerName: 'First Name',
            flex: 1,
            renderCell: (params) => {
                const displayName = params.value?.trim() || "(N/A)"; // fallback if null, undefined, or empty
                return (
                    <Link
                        to={`/leads/${params.row.id}`}
                        onClick={(e) => e.stopPropagation()}
                        style={{ color: 'inherit' }}
                    >
                        {displayName}
                    </Link>
                );
            },
        },
        { field: 'lastName', headerName: 'Last Name', flex: 1 },
        { field: 'title', headerName: 'Title', flex: 1 },
        { field: 'company', headerName: 'Company', flex: 1 },
        { field: 'email', headerName: 'Email', flex: 1 },
        { field: 'phone', headerName: 'Phone', flex: 1 },
        { field: 'mobile', headerName: 'Mobile', flex: 1 },
        { field: 'leadSource', headerName: 'Lead Source', flex: 1 },
        { field: 'ownerName', headerName: 'Lead Owner', flex: 1 },
    ];

    const transformData = (data) => {
        return data.map(item => ({
            id: item.id,
            firstName: item.firstName,
            lastName: item.lastName,
            title: item.title,
            company: item.company,
            email: item.email,
            phone: item.phone,
            mobile: item.mobile,
            leadSource: LeadSource?.[item.leadSource]?.label || '',
            ownerName: item.ownerName
        }));
    };

    // useEffect(() => {
    //     setFilterParams(filters);
    // }, [triggerFetch]);

    useEffect(() => {
        setFilterParams(filters);
    }, [filters]);

    useEffect(() => {
        const fetchAndSetData = async () => {
            setLoading(true);

            const currentRequestId = ++requestIdRef.current; // Track request order

            try {
                const operatorMapping = {
                    "contains": 2,
                    "equals": 0,
                    "starts with": 4,
                    "ends with": 5,
                    "is empty": 6,
                    "is not empty": 7,
                    // "is any of": Not directly mapped
                };

                const sortField = sortModel.length > 0 ? sortModel[0].field : 'createdAt'; // Default to 'firstName'
                const sortDescending = sortModel.length > 0 ? sortModel[0].sort === 'desc' : true;

                let url = `/leads/paged`;
                // gets leads that do not have this campaignId
                if (campaignId) {
                    url += `/campaign/${campaignId}/unassigned`;
                }

                const data = await post(url, {
                    pageNumber: paginationModel.page + 1,
                    pageSize: paginationModel.pageSize,
                    filters: filterParams,
                    sortField: sortField,
                    sortDescending: sortDescending
                });
                // Only update if this is the latest request
                if (currentRequestId === requestIdRef.current) {
                    setRows(transformData(data.items));
                    setRowCount(data.totalCount);
                }
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                if (currentRequestId === requestIdRef.current) {
                    setLoading(false);
                    if (setTriggerFetch) {
                        setTriggerFetch(false);
                    }
                }
            }
        };

        fetchAndSetData();
    }, [paginationModel, filterParams, sortModel, triggerFetch]);

    const handleRowClick = (params) => {
        navigate(`/leads/${params.id}`);
    };

    return (
        <DataGrid
            rows={rows}
            columns={columns}
            rowCount={rowCount}
            slots={{ toolbar: CustomToolbar }}
            paginationMode="server"
            sortingMode="server"
            columnVisibilityModel={{
                id: false,
            }}
            getRowId={(row) => row.id}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[50, 75, 100]}
            onSortModelChange={(newSortModel) => setSortModel(newSortModel)}
            filterMode="server"
            onFilterModelChange={(newFilterModel) => {
                setFilterModel(newFilterModel);
            }}
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={onSelectionModelChange}
            disableRowSelectionOnClick
            loading={loading}
            onRowClick={handleRowClick}
            onRowContextMenu={(params, event) => {
                // Allow the browser's default right-click context menu
                // by not calling event.preventDefault()
            }}
            slotProps={{
                row: {
                    onContextMenu: (event) => {
                        // Let the native context menu appear
                        // (optional: you can also capture row data here if needed)
                        console.log('Right-clicked row:', event.currentTarget);
                    },
                    style: { cursor: 'context-menu' },
                },
            }}
            sx={{
                height: '100%', // Fills the parent container
                '& .MuiDataGrid-virtualScroller': {
                    overflow: 'auto', // Ensures scrollbars appear when necessary
                },
            }}
        />

    );
};

export default LeadsDataGrid;
